// GAVION X2: objection library model (pure). Twin of public.objections (31_gavion_objections.sql).
import { foldText } from "../domain/leadFields";
import { TRADE_SECTORS } from "../domain/trades";

export type Objection = {
  id: number;
  key: string | null;
  /** Raw companies.trade value; null = every trade. */
  trade: string | null;
  objection: string;
  response: string;
  tags: string[];
  sort_order: number;
  active: boolean;
  helpful_count: number;
  created_by: number | null;
  updated_at: string;
};

export const OBJECTION_MAX = 300;
export const RESPONSE_MAX = 2000;
export const TAGS_MAX = 8;
export const TAG_MAX = 30;

const fold = (s: string | null | undefined) => foldText(s ?? "").trim();
const byOrder = (a: Objection, b: Objection) =>
  a.sort_order - b.sort_order || a.id - b.id;

/** Active objections for a lead: its trade first, then the generic ones; optional accent-free search. */
export function objectionsForLead(
  list: readonly Objection[],
  trade: string | null | undefined,
  query: string,
): Objection[] {
  const t = fold(trade);
  const q = fold(query);
  return list
    .filter((o) => o.active)
    .filter((o) => o.trade === null || (t !== "" && fold(o.trade) === t))
    .filter(
      (o) =>
        !q ||
        fold([o.objection, o.response, ...(o.tags ?? [])].join(" ")).includes(
          q,
        ),
    )
    .sort(
      (a, b) =>
        (a.trade === null ? 1 : 0) - (b.trade === null ? 1 : 0) ||
        byOrder(a, b),
    );
}

/** "all" | "generic" | a raw trade value. */
export type TradeFilter = string;

export function adminObjections(
  list: readonly Objection[],
  filter: TradeFilter,
  showInactive: boolean,
): Objection[] {
  return list
    .filter((o) => showInactive || o.active)
    .filter((o) =>
      filter === "all"
        ? true
        : filter === "generic"
          ? o.trade === null
          : o.trade === filter,
    )
    .sort((a, b) => Number(b.active) - Number(a.active) || byOrder(a, b));
}

export const tradeLabel = (trade: string, locale: string): string =>
  TRADE_SECTORS[trade]?.[locale === "en" ? "en" : "fr"] ?? trade;

export const tradeOptions = (
  locale: string,
): { value: string; label: string }[] =>
  Object.keys(TRADE_SECTORS)
    .map((value) => ({ value, label: tradeLabel(value, locale) }))
    .sort((a, b) => a.label.localeCompare(b.label, locale));

export type ObjectionForm = {
  /** "" = every trade. */
  trade: string;
  objection: string;
  response: string;
  /** Comma-separated. */
  tags: string;
  sort_order: string;
  active: boolean;
};

export const emptyObjectionForm = (): ObjectionForm => ({
  trade: "",
  objection: "",
  response: "",
  tags: "",
  sort_order: "0",
  active: true,
});

export const parseTags = (raw: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(",")) {
    const tag = part.trim();
    if (!tag || seen.has(fold(tag))) continue;
    seen.add(fold(tag));
    out.push(tag);
  }
  return out;
};

export const toObjectionForm = (o: Objection): ObjectionForm => ({
  trade: o.trade ?? "",
  objection: o.objection,
  response: o.response,
  tags: (o.tags ?? []).join(", "),
  sort_order: String(o.sort_order),
  active: o.active,
});

export type ObjectionData = Pick<
  Objection,
  "trade" | "objection" | "response" | "tags" | "sort_order" | "active"
>;

export const fromObjectionForm = (f: ObjectionForm): ObjectionData => ({
  trade: f.trade.trim() || null,
  objection: f.objection.trim(),
  response: f.response.trim(),
  tags: parseTags(f.tags),
  sort_order: Number(f.sort_order),
  active: f.active,
});

export type ObjectionFormErrors = Partial<
  Record<"objection" | "response" | "sort_order" | "tags", string>
>;

export function validateObjectionForm(f: ObjectionForm): ObjectionFormErrors {
  const e: ObjectionFormErrors = {};
  const text = (k: "objection" | "response", max: number) => {
    const v = f[k].trim();
    if (!v) e[k] = "required";
    else if (v.length > max) e[k] = "too_long";
  };
  text("objection", OBJECTION_MAX);
  text("response", RESPONSE_MAX);
  if (!/^-?\d{1,6}$/.test(f.sort_order.trim())) e.sort_order = "invalid";
  const tags = parseTags(f.tags);
  if (tags.length > TAGS_MAX) e.tags = "too_many";
  else if (tags.some((t) => t.length > TAG_MAX)) e.tags = "too_long";
  return e;
}

/** Unique violation from PostgREST (code / body.code / message) or the FakeRest twin. */
export const isDuplicateUse = (e: unknown): boolean => {
  const err = e as {
    code?: string;
    body?: { code?: string };
    message?: string;
  } | null;
  return (
    err?.code === "23505" ||
    err?.body?.code === "23505" ||
    /duplicate key|uq_objection_uses_once_per_lead/i.test(err?.message ?? "")
  );
};
